import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Bell,
  Briefcase,
  ClipboardList,
  Database,
  Euro,
  CreditCard,
  Gift,
  Gauge,
  HelpCircle,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
  X,
} from "lucide-react";
import { PremiumBrandMark } from "@/components/brand/PremiumBrandMark";
import { formatClientDisplayName } from "@/lib/streamcareShared";

const searchGroups = [
  ["clients", "Clients"],
  ["subscriptions", "Abonnements"],
  ["tickets", "Tickets"],
];

function GlobalSearch({ apiBase, authHeaders, testIdPrefix = "global-search" }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const cleaned = query.trim();
    if (cleaned.length < 2) {
      setResults(null);
      setLoading(false);
      return undefined;
    }
    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${apiBase}/search/global`, { headers: authHeaders(), params: { q: cleaned } });
        if (active) {
          setResults(data);
          setOpen(true);
        }
      } catch (error) {
        if (active) setResults({ total: 0, clients: [], subscriptions: [], tickets: [] });
      } finally {
        if (active) setLoading(false);
      }
    }, 220);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [apiBase, authHeaders, query]);

  const clearSearch = () => {
    setQuery("");
    setResults(null);
    setOpen(false);
  };

  const openResult = (result) => {
    navigate(result.url);
    clearSearch();
  };

  const hasResults = Boolean(results && results.total > 0);

  return (
    <div className="global-search" data-testid={testIdPrefix} onBlur={() => window.setTimeout(() => setOpen(false), 140)}>
      <div className="global-search-input-wrap" data-testid={`${testIdPrefix}-input-wrap`}>
        <Search size={16} aria-hidden="true" data-testid={`${testIdPrefix}-icon`} />
        <input
          className="global-search-input"
          type="search"
          placeholder="Rechercher par nom, téléphone, e-mail ou référence…"
          value={query}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(event) => { if (event.key === "Escape") clearSearch(); }}
          data-testid={`${testIdPrefix}-input`}
          aria-label="Recherche globale"
        />
        {loading && <span className="global-search-loader" data-testid={`${testIdPrefix}-loading`}>Recherche…</span>}
        {query && <button type="button" className="global-search-clear" onMouseDown={(event) => event.preventDefault()} onClick={clearSearch} data-testid={`${testIdPrefix}-clear-button`} aria-label="Effacer la recherche"><X size={15} /></button>}
      </div>
      {open && query.trim().length >= 2 && <div className="global-search-panel" data-testid={`${testIdPrefix}-results-panel`}>
        <div className="global-search-panel-head" data-testid={`${testIdPrefix}-results-head`}>
          <span data-testid={`${testIdPrefix}-results-title`}>Résultats rapides</span>
          <b data-testid={`${testIdPrefix}-results-count`}>{results?.total || 0}</b>
        </div>
        {!loading && !hasResults && <div className="global-search-empty" data-testid={`${testIdPrefix}-empty`}>Aucun résultat trouvé</div>}
        {hasResults && searchGroups.map(([key, label]) => (results?.[key] || []).length > 0 && (
          <section className="global-search-group" key={key} data-testid={`${testIdPrefix}-group-${key}`}>
            <p className="global-search-group-title" data-testid={`${testIdPrefix}-group-title-${key}`}>{label}</p>
            {(results[key] || []).map((item) => <button type="button" key={`${item.type}-${item.id}`} className="global-search-result" onMouseDown={(event) => event.preventDefault()} onClick={() => openResult(item)} data-testid={`${testIdPrefix}-result-${item.type}-${item.id}`}>
              <span className={`global-search-type global-search-type-${item.type}`} data-testid={`${testIdPrefix}-result-type-${item.type}-${item.id}`}>{item.type === "client" ? "Client" : item.type === "subscription" ? "Abo" : "Ticket"}</span>
              <span className="global-search-copy" data-testid={`${testIdPrefix}-result-copy-${item.type}-${item.id}`}>
                <strong data-testid={`${testIdPrefix}-result-title-${item.type}-${item.id}`}>{item.title}</strong>
                <small data-testid={`${testIdPrefix}-result-subtitle-${item.type}-${item.id}`}>{item.subtitle}</small>
                {item.meta && <em data-testid={`${testIdPrefix}-result-meta-${item.type}-${item.id}`}>{item.meta}</em>}
              </span>
              {item.badge && <span className="badge badge-neutral global-search-badge" data-testid={`${testIdPrefix}-result-badge-${item.type}-${item.id}`}>{item.badge}</span>}
            </button>)}
          </section>
        ))}
      </div>}
    </div>
  );
}

function NotificationBell({ apiBase, authHeaders, testIdPrefix = "notification" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const loadNotifications = useCallback(async () => {
    try {
      const list = await axios.get(`${apiBase}/notifications`, { headers: authHeaders(), params: { limit: 12 }, withCredentials: true });
      setNotifications(list.data || []);
      try {
        const summary = await axios.get(`${apiBase}/notifications/unread-summary`, { headers: authHeaders(), withCredentials: true });
        setUnreadCount(summary.data?.unread_count || 0);
      } catch (summaryError) {
        setUnreadCount((list.data || []).filter((notification) => !notification.read).length);
      }
    } catch (error) {
      setUnreadCount(0);
      setNotifications([]);
    }
  }, [apiBase, authHeaders]);

  useEffect(() => {
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 45000);
    window.addEventListener("streamcare-notifications-updated", loadNotifications);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("streamcare-notifications-updated", loadNotifications);
    };
  }, [loadNotifications]);

  const markRead = async (notification, follow = false) => {
    try {
      if (!notification.read) await axios.patch(`${apiBase}/notifications/${notification.id}/read`, { read: true }, { headers: authHeaders(), withCredentials: true });
      await loadNotifications();
      if (follow && notification.action_url) {
        setOpen(false);
        navigate(notification.action_url);
      }
    } catch (error) {
      await loadNotifications();
    }
  };

  return (
    <div className="relative" data-testid={`${testIdPrefix}-bell-wrap`}>
      <button type="button" className="icon-btn relative" onClick={() => { setOpen(!open); loadNotifications(); }} data-testid={`${testIdPrefix}-bell-button`} aria-label="Notifications">
        <Bell size={18} />
        {unreadCount > 0 && <span className="absolute -right-1 -top-1 grid min-h-[18px] min-w-[18px] place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid={`${testIdPrefix}-unread-badge`}>{unreadCount}</span>}
      </button>
      {open && <div className="fixed right-4 top-20 z-[120] w-[min(420px,calc(100vw-2rem))] rounded-md border border-white/10 bg-[#101624] p-3 shadow-2xl" data-testid={`${testIdPrefix}-dropdown`}>
        <div className="flex items-center justify-between gap-2 border-b border-white/10 pb-2" data-testid={`${testIdPrefix}-dropdown-header`}><p className="font-[Outfit] text-base text-white" data-testid={`${testIdPrefix}-dropdown-title`}>Notifications</p><button type="button" className="btn-secondary" onClick={loadNotifications} data-testid={`${testIdPrefix}-refresh-button`}><RefreshCw size={14} /> Actualiser</button></div>
        <div className="mt-3 grid max-h-[min(420px,calc(100vh-10rem))] gap-2 overflow-y-auto" data-testid={`${testIdPrefix}-list`}>
          {notifications.length === 0 ? <p className="rounded-md border border-white/10 bg-white/5 p-3 text-sm text-slate-400" data-testid={`${testIdPrefix}-empty`}>Aucune notification.</p> : notifications.map((notification) => <article key={notification.id} className={`rounded-md border p-3 ${notification.read ? "border-white/10 bg-white/5" : "border-[#d4af37]/40 bg-[#d4af37]/10"}`} data-testid={`${testIdPrefix}-item-${notification.id}`}><div className="flex items-start justify-between gap-2"><div><p className="font-[Outfit] text-sm text-white" data-testid={`${testIdPrefix}-title-${notification.id}`}>{notification.title}</p><p className="mt-1 text-xs text-slate-400" data-testid={`${testIdPrefix}-date-${notification.id}`}>{new Date(notification.created_at).toLocaleString("fr-FR")}</p></div>{!notification.read && <span className="badge badge-amber" data-testid={`${testIdPrefix}-unread-chip-${notification.id}`}>Non lu</span>}</div><p className="mt-2 text-sm text-slate-300" data-testid={`${testIdPrefix}-message-${notification.id}`}>{notification.message}</p><div className="mt-3 flex flex-wrap gap-2">{notification.action_url && <button type="button" className="btn-primary" onClick={() => markRead(notification, true)} data-testid={`${testIdPrefix}-open-${notification.id}`}>Voir</button>}<button type="button" className="btn-secondary" onClick={() => markRead(notification, false)} data-testid={`${testIdPrefix}-mark-read-${notification.id}`}>{notification.read ? "Lu" : "Marquer comme lu"}</button></div></article>)}
        </div>
      </div>}
    </div>
  );
}

function Sidebar({ user, onLogout, open, setOpen, apiBase, authHeaders }) {
  const location = useLocation();
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [subscriptionRequestSignals, setSubscriptionRequestSignals] = useState({ open: 0, unread: 0 });
  const [adminSignals, setAdminSignals] = useState({ tickets: 0, urgent: 0, expirations: 0 });
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [clientProfile, setClientProfile] = useState(null);
  const logoutTriggeredRef = useRef(false);
  const sidebarDisplayName = formatClientDisplayName(clientProfile, user);

  const loadUnreadAnnouncements = useCallback(async () => {
    if (user.role !== "client") return;
    try {
      const { data } = await axios.get(`${apiBase}/announcements/unread-summary`, { headers: authHeaders() });
      setUnreadAnnouncements(data.unread_count || 0);
    } catch (error) {
      setUnreadAnnouncements(0);
    }
  }, [apiBase, authHeaders, user.role]);

  const loadClientProfile = useCallback(async () => {
    if (user.role !== "client") return;
    try {
      const { data } = await axios.get(`${apiBase}/clients`, { headers: authHeaders() });
      setClientProfile((data || [])[0] || null);
    } catch (error) {
      setClientProfile(null);
    }
  }, [apiBase, authHeaders, user.role]);

  const loadAdminSignals = useCallback(async () => {
    if (!["admin", "support"].includes(user.role)) return;
    try {
      const [ticketsResponse, subscriptionsResponse] = await Promise.all([
        axios.get(`${apiBase}/tickets`, { headers: authHeaders() }),
        axios.get(`${apiBase}/subscriptions`, { headers: authHeaders() }),
      ]);
      const tickets = ticketsResponse.data || [];
      const subscriptions = subscriptionsResponse.data || [];
      const openStatuses = ["nouveau", "en_cours", "en_attente"];
      const latestRole = (ticket) => ticket.lastMessageBy || [...(ticket.public_notes || [])].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)).at(-1)?.author_role;
      const waitingFor = (ticket) => {
        if (["resolu", "ferme"].includes(ticket.status)) return "none";
        if (["support", "client", "none"].includes(ticket.waitingFor)) return ticket.waitingFor;
        if (ticket.status === "en_attente") return "client";
        return ["admin", "support"].includes(latestRole(ticket)) ? "client" : "support";
      };
      const unread = tickets.filter((ticket) => openStatuses.includes(ticket.status) && waitingFor(ticket) === "support" && (ticket.isRead === false || ticket.isReadByAdmin === false)).length;
      const urgent = tickets.filter((ticket) => openStatuses.includes(ticket.status) && ["haute", "critique"].includes(ticket.priority)).length;
      const now = new Date();
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      const expirations = subscriptions.filter((subscription) => {
        const raw = subscription.expiration_date || subscription.renewal_date;
        if (!raw || !["active", "actif"].includes(subscription.status)) return false;
        const diff = new Date(`${raw}T23:59:59`).getTime() - now.getTime();
        return diff >= 0 && diff <= sevenDays;
      }).length;
      setAdminSignals({ tickets: unread, urgent, expirations });
    } catch (error) {
      setAdminSignals({ tickets: 0, urgent: 0, expirations: 0 });
    }
  }, [apiBase, authHeaders, user.role]);

  const loadUnreadTickets = useCallback(async () => {
    if (user.role !== "client") return;
    try {
      const { data } = await axios.get(`${apiBase}/tickets/unread-summary`, { headers: authHeaders() });
      setUnreadTickets(data.unread_count || 0);
    } catch (error) {
      setUnreadTickets(0);
    }
  }, [apiBase, authHeaders, user.role]);

  const loadPendingRequests = useCallback(async () => {
    if (user.role !== "client") return;
    try {
      const [attachments, renewals, subscriptions] = await Promise.all([
        axios.get(`${apiBase}/subscription-attachment-requests`, { headers: authHeaders() }),
        axios.get(`${apiBase}/renewal-requests`, { headers: authHeaders() }),
        axios.get(`${apiBase}/subscription-requests/summary`, { headers: authHeaders() }).catch(() => ({ data: { open_count: 0, unread_count: 0 } })),
      ]);
      const attachmentCount = (attachments.data || []).filter((item) => ["en_attente", "en_verification", "infos_demandees"].includes(item.status)).length;
      const renewalCount = (renewals.data || []).filter((item) => ["en_attente", "en_verification", "infos_demandees"].includes(item.status)).length;
      const subscriptionCount = Number(subscriptions.data?.open_count || 0) + Number(subscriptions.data?.unread_count || 0);
      setPendingRequests(attachmentCount + renewalCount + subscriptionCount);
    } catch (error) {
      setPendingRequests(0);
    }
  }, [apiBase, authHeaders, user.role]);

  const loadSubscriptionRequestSignals = useCallback(async () => {
    if (!["admin", "support"].includes(user.role)) return;
    try {
      const { data } = await axios.get(`${apiBase}/subscription-requests/summary`, { headers: authHeaders() });
      setSubscriptionRequestSignals({ open: data.open_count || 0, unread: data.unread_count || 0 });
    } catch (error) {
      setSubscriptionRequestSignals({ open: 0, unread: 0 });
    }
  }, [apiBase, authHeaders, user.role]);

  useEffect(() => {
    loadUnreadAnnouncements();
    loadUnreadTickets();
    loadPendingRequests();
    loadAdminSignals();
    loadClientProfile();
    loadSubscriptionRequestSignals();
    const unreadTimer = user.role === "client" ? window.setInterval(loadUnreadTickets, 45000) : null;
    const adminTimer = ["admin", "support"].includes(user.role) ? window.setInterval(loadAdminSignals, 45000) : null;
    const requestTimer = ["admin", "support"].includes(user.role) ? window.setInterval(loadSubscriptionRequestSignals, 45000) : null;
    window.addEventListener("streamcare-announcements-updated", loadUnreadAnnouncements);
    window.addEventListener("streamcare-tickets-updated", loadUnreadTickets);
    window.addEventListener("streamcare-tickets-updated", loadAdminSignals);
    window.addEventListener("streamcare-requests-updated", loadPendingRequests);
    window.addEventListener("streamcare-requests-updated", loadSubscriptionRequestSignals);
    return () => {
      if (unreadTimer) window.clearInterval(unreadTimer);
      if (adminTimer) window.clearInterval(adminTimer);
      if (requestTimer) window.clearInterval(requestTimer);
      window.removeEventListener("streamcare-announcements-updated", loadUnreadAnnouncements);
      window.removeEventListener("streamcare-tickets-updated", loadUnreadTickets);
      window.removeEventListener("streamcare-tickets-updated", loadAdminSignals);
      window.removeEventListener("streamcare-requests-updated", loadPendingRequests);
      window.removeEventListener("streamcare-requests-updated", loadSubscriptionRequestSignals);
    };
  }, [loadAdminSignals, loadClientProfile, loadPendingRequests, loadSubscriptionRequestSignals, loadUnreadAnnouncements, loadUnreadTickets, user.role]);

  const navigationGroups = useMemo(() => user.role === "client"
    ? [
      { id: "client-main", label: "Espace client", items: [
        { path: "/dashboard", label: "Tableau de bord", icon: Gauge, visible: true },
        { path: "/services", label: "Mes abonnements", icon: Briefcase, visible: true },
        { path: "/tickets", label: "Mes tickets", icon: MessageSquare, visible: true, badge: unreadTickets, badgeTestId: "sidebar-tickets-unread-badge" },
        { path: "/announcements", label: "Annonces", icon: Bell, visible: true, badge: unreadAnnouncements, badgeTestId: "sidebar-announcements-unread-badge" },
        { path: "/requests", label: "Mes demandes", icon: ClipboardList, visible: true, badge: pendingRequests, badgeTone: "amber", badgeTestId: "sidebar-requests-pending-badge" },
        { path: "/faq", label: "FAQ", icon: HelpCircle, visible: true },
        { path: "/profile", label: "Mon profil", icon: UserRound, visible: true },
      ] },
    ]
    : [
      { id: "pilotage", label: "Pilotage", items: [
        { path: "/dashboard", label: "Tableau de bord", icon: Gauge, visible: true, badge: adminSignals.tickets, badgeTestId: "sidebar-dashboard-alert-badge" },
      ] },
      { id: "clients", label: "Clients", items: [
        { path: "/clients", label: "Clients", icon: Users, visible: true },
        { path: "/abonnements", label: "Abonnements", icon: Briefcase, visible: true, aliases: ["/subscriptions"] },
        { path: "/subscription-requests", label: "Demandes d’abonnement", icon: ClipboardList, visible: true, badge: subscriptionRequestSignals.open + subscriptionRequestSignals.unread, badgeTone: "amber", badgeTestId: "subscription-request-badge", testId: "nav-subscription-requests" },
        { path: "/renewals", label: "Renouvellements", icon: RefreshCw, visible: user.role === "admin" },
      ] },
      { id: "support", label: "Support", items: [
        { path: "/tickets", label: "Tickets", icon: MessageSquare, visible: true, badge: adminSignals.tickets, badgeTestId: "sidebar-admin-tickets-badge" },
        { path: "/announcements", label: "Annonces", icon: Bell, visible: true },
        { path: "/email-previews", label: "Aperçus e-mails", icon: Mail, visible: ["admin", "support"].includes(user.role) },
        { type: "group", id: "demandes", label: "Demandes", icon: ClipboardList, visible: true, children: [
          { path: "/tests", label: "Demandes de test", icon: ClipboardList, visible: true },
          { path: "/attachment-requests", label: "Rattachements", icon: ClipboardList, visible: user.role === "admin" },
        ] },
      ] },
      { id: "catalogue", label: "Catalogue", items: [
        { path: "/services", label: "Services", icon: Briefcase, visible: true },
        { path: "/supplier-management", label: "Fournisseurs", icon: Database, visible: user.role === "admin" },
      ] },
      { id: "finance", label: "Finance", items: [
        { path: "/finance", label: "Vue Finance", icon: Euro, visible: user.role === "admin" },
        { path: "/paypal-payments", label: "Paiements", icon: CreditCard, visible: user.role === "admin" },
        { path: "/loyalty", label: "Fidélité", icon: Gift, visible: user.role === "admin" },
      ] },
      { id: "administration", label: "Administration", items: [
        { path: "/settings", label: "Paramètres", icon: Settings, visible: ["admin", "support"].includes(user.role) },
        { path: "/audit", label: "Journal d’activité", icon: ShieldCheck, visible: ["admin", "support"].includes(user.role) },
        { path: "/faq-bot", label: "Centre d’aide", icon: HelpCircle, visible: user.role === "admin" },
      ] },
    ], [adminSignals.tickets, pendingRequests, subscriptionRequestSignals.open, subscriptionRequestSignals.unread, unreadAnnouncements, unreadTickets, user.role]);
  const visibleGroups = useMemo(() => navigationGroups.map((group) => ({ ...group, items: group.items.map((item) => item.type === "group" ? { ...item, children: item.children.filter((child) => child.visible) } : item).filter((item) => item.visible && (item.type !== "group" || item.children.length > 0)) })).filter((group) => group.items.length > 0), [navigationGroups]);
  const isActiveItem = useCallback((item) => item.path === location.pathname || (item.aliases || []).includes(location.pathname), [location.pathname]);
  const isGroupActive = useCallback((group) => group.items.some((item) => item.type === "group" ? item.children.some(isActiveItem) : isActiveItem(item)), [isActiveItem]);
  const isTopGroupOpen = useCallback((group) => isGroupActive(group) || !collapsedGroups[group.id], [collapsedGroups, isGroupActive]);
  const isSubGroupOpen = useCallback((item) => item.children?.some(isActiveItem) || !collapsedGroups[item.id], [collapsedGroups, isActiveItem]);
  const toggleSubGroup = (itemId) => setCollapsedGroups((current) => ({ ...current, [itemId]: !current[itemId] }));
  const closeAfterNavigate = () => setOpen(false);
  const testSlug = (value) => value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const renderBadge = (item) => item.badge > 0 ? <span className={`badge ${item.badgeTone === "amber" ? "badge-amber" : "badge-red"}`} data-testid={item.badgeTestId}>{item.badge}</span> : null;
  const renderNavItem = (item, nested = false) => {
    const Icon = item.icon;
    return <NavLink key={item.path} to={item.path} className={`nav-link ${nested ? "nav-link-child" : ""}`} data-testid={item.testId || `nav-link-${testSlug(item.label)}`} onClick={closeAfterNavigate} aria-label={item.label} end={item.path === "/dashboard"}>
      <Icon size={nested ? 15 : 17} aria-hidden="true" /> <span className="flex-1" data-testid={`nav-label-${testSlug(item.label)}`}>{item.label}</span>{renderBadge(item)}
    </NavLink>;
  };
  const isClientPortal = user.role === "client";
  const brandTitle = "IPTVISIONS";
  const brandSubtitle = isClientPortal ? "Espace client" : "Panel interne";
  const handleLogout = useCallback((event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (logoutTriggeredRef.current) return;
    logoutTriggeredRef.current = true;
    setOpen(false);
    onLogout();
    window.setTimeout(() => { logoutTriggeredRef.current = false; }, 600);
  }, [onLogout, setOpen]);

  const clientBottomNavItems = [
    { path: "/dashboard", label: "Accueil", icon: Gauge, testId: "bottom-nav-home" },
    { path: "/services", label: "Abonnements", icon: Briefcase, testId: "bottom-nav-services" },
    { path: "/requests", label: "Demandes", icon: ClipboardList, badge: pendingRequests, testId: "bottom-nav-requests" },
    { path: "/tickets", label: "Support", icon: MessageSquare, badge: unreadTickets, testId: "bottom-nav-support" },
    { path: "/profile", label: "Profil", icon: UserRound, testId: "bottom-nav-profile" },
  ];

  return (
    <>
      <div className={`drawer-backdrop ${open ? "open" : ""}`} onPointerDown={() => setOpen(false)} onClick={() => setOpen(false)} data-testid="mobile-menu-backdrop" />
      <aside className={`sidebar ${open ? "open" : ""}`} data-state={open ? "open" : "closed"} data-testid="main-sidebar">
        <div className="flex items-center justify-between gap-3" data-testid="sidebar-brand-row">
          <div className="sidebar-brand-lockup" data-testid="sidebar-brand">
            <PremiumBrandMark size="sidebar" testId="sidebar-brand-logo" label={brandTitle} />
            <div>
              <p className="text-xs text-slate-500" data-testid="sidebar-brand-subtitle">{brandSubtitle}</p>
            </div>
          </div>
          <button type="button" className="icon-btn md:hidden" onPointerDown={() => setOpen(false)} onClick={() => setOpen(false)} data-testid="sidebar-close-button" aria-label="Fermer"><X size={18} /></button>
        </div>
        <nav className="sidebar-navigation" data-testid="sidebar-navigation" aria-label="Navigation principale">
          {visibleGroups.map((group) => <section key={group.id} className={`sidebar-nav-section ${isGroupActive(group) ? "is-active" : ""}`} data-testid={`nav-section-${group.id}`}>
            <button type="button" className="sidebar-nav-heading" onClick={() => toggleSubGroup(group.id)} aria-expanded={isTopGroupOpen(group)} data-testid={`nav-section-heading-${group.id}`}>{group.label}<span aria-hidden="true">{isTopGroupOpen(group) ? "−" : "+"}</span></button>
            {isTopGroupOpen(group) && <div className="sidebar-nav-items" data-testid={`nav-section-items-${group.id}`}>{group.items.map((item) => item.type === "group" ? <div key={item.id} className={`sidebar-subgroup ${isSubGroupOpen(item) ? "open" : ""}`} data-testid={`nav-subgroup-${item.id}`}>
              <button type="button" className="nav-link nav-subgroup-toggle" onClick={() => toggleSubGroup(item.id)} aria-expanded={isSubGroupOpen(item)} data-testid={`nav-subgroup-toggle-${item.id}`}><item.icon size={17} aria-hidden="true" /><span className="flex-1">{item.label}</span><span aria-hidden="true" className="nav-chevron">{isSubGroupOpen(item) ? "−" : "+"}</span></button>
              {isSubGroupOpen(item) && <div className="sidebar-subgroup-items" data-testid={`nav-subgroup-items-${item.id}`}>{item.children.map((child) => renderNavItem(child, true))}</div>}
            </div> : renderNavItem(item))}</div>}
          </section>)}
        </nav>
        <div className="sidebar-user-card mt-auto surface p-3" data-testid="sidebar-user-card">
          <p className="sidebar-user-name" title={sidebarDisplayName} data-testid="sidebar-user-fullname">{sidebarDisplayName}</p>
          <p className="sidebar-user-role" data-testid="sidebar-user-role">{user.role === "client" ? "Client" : user.role}</p>
          <button type="button" className="btn-secondary mt-3 flex w-full items-center justify-center gap-2" onPointerDown={handleLogout} onClick={handleLogout} data-testid="sidebar-logout-button"><LogOut size={16} /> Déconnexion</button>
        </div>
      </aside>
      {isClientPortal && <nav className="client-bottom-nav" data-testid="client-bottom-nav" aria-label="Navigation client mobile">{clientBottomNavItems.map((item) => { const Icon = item.icon; const active = location.pathname === item.path || (item.path !== "/dashboard" && location.pathname.startsWith(item.path)); return <NavLink key={item.path} to={item.path} className={`client-bottom-nav-item ${active ? "active" : ""}`} data-testid={item.testId} onClick={closeAfterNavigate}><span className="client-bottom-nav-icon"><Icon size={19} />{item.badge > 0 && <em data-testid={`${item.testId}-badge`}>{item.badge}</em>}</span><span>{item.label}</span></NavLink>; })}</nav>}
    </>
  );
}

export function AppShell({ user, onLogout, apiBase, authHeaders, children }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("drawer-open", open);
    return () => document.body.classList.remove("drawer-open");
  }, [open]);

  return (
    <div className={`premium-shell layout ${user.role === "client" ? "client-shell" : ""}`} data-testid="app-shell">
      <Sidebar user={user} onLogout={onLogout} open={open} setOpen={setOpen} apiBase={apiBase} authHeaders={authHeaders} />
      <main className="main-area" data-testid="main-content">
        <div className="mobile-topbar" data-testid="mobile-topbar">
          <button type="button" className="icon-btn" onPointerDown={() => setOpen(true)} onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Menu" aria-expanded={open}><Menu size={20} /></button>
          <PremiumBrandMark size="mobile" testId="mobile-brand-logo" label="IPTVISIONS" />
          <NotificationBell apiBase={apiBase} authHeaders={authHeaders} testIdPrefix="mobile-notification" />
        </div>
        {user.role !== "client" && <div className="mobile-admin-global-search md:hidden" data-testid="mobile-admin-global-search-wrap"><GlobalSearch apiBase={apiBase} authHeaders={authHeaders} testIdPrefix="mobile-global-search" /></div>}
        <div className={`${user.role === "client" ? "mb-2" : "mb-5"} hidden items-start justify-end gap-3 md:flex`} data-testid="desktop-header-actions">
          <NotificationBell apiBase={apiBase} authHeaders={authHeaders} testIdPrefix="desktop-notification" />
          {user.role !== "client" && <GlobalSearch apiBase={apiBase} authHeaders={authHeaders} />}
        </div>
        {children}
      </main>
    </div>
  );
}